import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

const PRODUCT_PATH = /\/products\//;
const MAIN_PRODUCT_ROOT = '.product-information, [data-testid="product-information"]';
const EXCLUDED_ADD_ROOT = '#quick-add-dialog, .quick-add-modal, quick-add-component, product-card';

const CART_PULSE_MS = 400;
const CASA_ADD_SOURCE = 'casa-product-form';

/**
 * @param {DOMRect} rect
 * @returns {boolean}
 */
function isRectInViewport(rect) {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

/**
 * @param {Event} event
 * @returns {Element | null}
 */
function cartFlySource(event) {
  const origin = event.target;
  if (!(origin instanceof Element)) return null;
  const casaButton = origin.querySelector('[data-casa-add-button]');
  if (casaButton) return casaButton;
  const added = [...document.querySelectorAll('[data-added="true"]')];
  return added.find((button) => isRectInViewport(button.getBoundingClientRect())) ?? added[0] ?? origin.querySelector('[type="submit"]');
}

/**
 * @param {Element} cartIcon
 */
function pulseCartIcon(cartIcon) {
  cartIcon.classList.add('wc-cart-arrived');
  setTimeout(() => cartIcon.classList.remove('wc-cart-arrived'), CART_PULSE_MS);
}

/**
 * @param {Element} source
 * @param {Element} cartIcon
 */
function animateCartFlyDot(source, cartIcon) {
  const from = source.getBoundingClientRect();
  const to = cartIcon.getBoundingClientRect();
  const startX = from.left + from.width / 2;
  const startY = from.top + from.height / 2;
  const dot = document.createElement('span');
  dot.className = 'wc-cart-fly-dot';
  dot.style.left = `${startX}px`;
  dot.style.top = `${startY}px`;
  dot.style.setProperty('--travel-x', `${to.left + to.width / 2 - startX}px`);
  dot.style.setProperty('--travel-y', `${to.top + to.height / 2 - startY}px`);
  document.body.appendChild(dot);
  dot.addEventListener(
    'animationend',
    () => {
      dot.remove();
      pulseCartIcon(cartIcon);
    },
    { once: true }
  );
}

/**
 * @param {Event} event
 */
function flyCircleToCart(event) {
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  if (!cartIcon) return;
  if (prefersReducedMotion()) {
    pulseCartIcon(cartIcon);
    return;
  }
  const source = cartFlySource(event);
  if (!source) {
    pulseCartIcon(cartIcon);
    return;
  }
  animateCartFlyDot(source, cartIcon);
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isLocaleProductPath(pathname) {
  return PRODUCT_PATH.test(pathname);
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
export function isSuccessfulProductAdd(event) {
  if (!(event instanceof CartAddEvent)) return false;
  if (event.detail?.data?.didError) return false;
  return event.detail?.data?.source === 'product-form-component';
}

/**
 * Casa featured-card adds open their own confirmation modal instead of the drawer or fly dot.
 * @param {Event} event
 * @returns {boolean}
 */
export function isCasaProductFormAdd(event) {
  return event instanceof CartAddEvent && event.detail?.data?.source === CASA_ADD_SOURCE;
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
export function isMainProductPageAdd(event) {
  if (!isSuccessfulProductAdd(event)) return false;
  const origin = event.target;
  if (!(origin instanceof Element)) return false;
  if (origin.closest(EXCLUDED_ADD_ROOT)) return false;
  return Boolean(origin.closest(MAIN_PRODUCT_ROOT));
}

/**
 * @param {Event} event
 * @param {string} [pathname]
 * @returns {boolean}
 */
export function shouldOpenMobileProductDrawer(event, pathname = location.pathname) {
  if (!isLocaleProductPath(pathname)) return false;
  return isMainProductPageAdd(event);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    if (isCasaProductFormAdd(event)) {
      this.#announceCartCount(event.detail.resource?.item_count);
      return;
    }
    if (isMobileBreakpoint()) {
      this.#handleMobileCartAdd(event);
    } else if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * @param {CustomEvent<{ data?: { didError?: boolean } }>} event
   */
  #handleMobileCartAdd(event) {
    if (this.hasAttribute('auto-open') && shouldOpenMobileProductDrawer(event)) {
      this.showDialog();
      return;
    }
    flyCircleToCart(event);
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    // The summary is a pinned flex sibling of the scrolling items, so it stays sticky whenever it exists.
    const summary = dialog.querySelector('.cart-drawer__summary');
    dialog.setAttribute('cart-summary-sticky', summary ? 'true' : 'false');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
